import React, {useState, FC, useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup,
    Typography
} from "@material-ui/core";
import {ErrorMessage, Field, Form, Formik} from "formik";
import {TextField} from "formik-material-ui";
import * as Yup from "yup";
import {colorError, colorPrimary, colorWhite} from "../../../../Constants/theme";
import DatePicker from "../../../DatePicker";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import moment from "moment/moment";
import {chronicConditions} from "../../../../Constants/chronicConditions";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import {addConditionFormTitles} from "../../../../Constants/conditionTypes";

const useStyles = makeStyles({
    dialog: {
        padding: "20px"
    },
    subtitle: {
        fontSize: 18
    },
    formItem: {
        marginBottom: "30px",
    },
    button: {
        margin: "1.5em 1.5em 0 0",
        padding: "13px 28px",
    },
    deleteButton: {
        margin: "1.5em 1.5em 0 0",
        padding: "13px 28px",
        border: "1.5px solid #FF6D6D",
        color: "#EA5858"
    },
    formError: {
        color: colorError,
        fontSize: "12px"
    },
    groupLabel: {
        color: colorPrimary,
        backgroundColor: "#F2F2F2",
        fontWeight: 700
    },
    groupItem: {
        fontSize: 18,
        color: "#000000",
        backgroundColor: `${colorWhite} !important`
    },
    inputStyle: {
        height: 48,
        "& > input": {
            padding: "0px 4px !important"
        }
    }
});

const fields = {
    ALLERGY: "allergies",
    CHRONIC_CONDITION: "chronicConditions",
    PREVENTATIVES: "medications",
    MEDICATIONS: "medications",
};

export const viewConditionFormTitles = {
    ALLERGY: "Allergic Reaction Details",
    CHRONIC_CONDITION: "Health Issue Details",
    PREVENTATIVES: "Preventative Details",
    MEDICATIONS: "Medication Details",
};

const nameFieldTitles = {
    ALLERGY: "Allergy to",
    CHRONIC_CONDITION: "Condition Name",
    PREVENTATIVES: "Drug Name",
    MEDICATIONS: "Drug Name",
};

interface AllergiesIllnessesFormProps {
    isOpen: boolean,
    setOpenDialog: (boolean) => void,
    type: string,
    handleSubmit: (any) => void,
    itemToEdit: any,
    setItemToEdit: (any) => void,
    handleDelete: (any) => void,
    selectedAnimal?: any,
}

const AllergiesIllnessesForm: React.FC<AllergiesIllnessesFormProps> = (
    {
        isOpen,
        setOpenDialog,
        type,
        handleSubmit = null,
        itemToEdit = null,
        setItemToEdit = null,
        handleDelete = null,
        selectedAnimal = null
    }) => {
    const classes = useStyles();
    const [initialValues, setInitialValues] = useState({});
    const [formikValues, setFormikValues] = useState(null);
    const [isCurrent, setIsCurrent] = useState(true);
    const [startDate, setStartDate] = useState({year: "", month: ""});
    const [endDate, setEndDate] = useState({year: "", month: ""});
    const [dateErr, setDateErr] = useState(false);
    const pet = useSelector((state: RootState) => state?.pets.selectedPet);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Required"),
    });

    useEffect(() => {
        const now = moment();
        const start = now.month(startDate.month).year(Number(startDate.year)).valueOf();
        const end = now.month(endDate.month).year(Number(endDate.year)).valueOf();
        setDateErr(end < start);
    }, [endDate, startDate]);

    useEffect(() => {
        let initialValues;
        switch (type) {
            case "ALLERGY":
                initialValues = {
                    name: "",
                    startMonth: "",
                    startYear: "",
                    notes: "",
                    isActive: "true",
                };
                break;
            case "CHRONIC_CONDITION":
                initialValues = {
                    name: "",
                    startMonth: "",
                    startYear: "",
                    endMonth: "",
                    endYear: "",
                    isActive: "true",
                };
                break;
            case "PREVENTATIVES":
            case "MEDICATIONS":
                initialValues = {
                    name: "",
                    startMonth: "",
                    startYear: "",
                    endMonth: "",
                    endYear: "",
                    amount: "",
                    howOften: "",
                    isActive: "true",
                };
                break;
        }
        setInitialValues({...initialValues});
        setFormikValues({...initialValues});
        setIsCurrent(true);
    }, [type]);

    const datePickerInputProps = {
        style: {
            height: 48,
            color: "#000000",
            fontSize: 18
        },
        endAdornment: (
            <InputAdornment position="start">
                <CalendarTodayIcon fontSize="small"/>
            </InputAdornment>
        ),
        startAdornment: (
            <InputAdornment position="start">
                <ExpandMoreIcon fontSize="small"/>
            </InputAdornment>
        )
    };

    const handleClose = () => {
        if (itemToEdit) {
            setItemToEdit(null);
        }
        setOpenDialog(false);
    };

    useEffect(() => {
        setOpenDialog(isOpen);
    }, [isOpen]);

    useEffect(() => {
        if (itemToEdit) {
            const fieldName = fields[type];
            const {date, endDate, isActive, ...rest} = selectedAnimal ? selectedAnimal.allergies.find(medicine => medicine.name === itemToEdit) : pet[fieldName].find(medicine => medicine.name === itemToEdit);
            setIsCurrent(isActive);
            const newFormikValues = {
                startMonth: date || "",
                startYear: date || "",
                isActive: isActive.toString(),
                ...rest,
            };
            if (!isActive) {
                newFormikValues.endMonth = endDate || "";
                newFormikValues.endYear = endDate || "";
            }
            setFormikValues({...newFormikValues});
        } else {
            setIsCurrent(true);
            setFormikValues({...initialValues});
        }
    }, [itemToEdit]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            classes={{paper: classes.dialog}}
            scroll="body"
            disableEnforceFocus
        >
            <DialogTitle color="primary" disableTypography>
                <Typography variant="h3">
                    {itemToEdit ? addConditionFormTitles[type] : viewConditionFormTitles[type]}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Formik
                    initialValues={formikValues}
                    enableReinitialize
                    onSubmit={(values) => {
                        let condition;
                        if (type !== "ALLERGY") {
                            const {
                                startMonth,
                                startYear,
                                endMonth,
                                endYear,
                                isActive,
                                ...rest
                            } = values;
                            if (isActive === "false") {
                                if (dateErr) return;
                            }
                            condition = {
                                ...rest,
                                isActive: isActive === "true",
                                date: moment().month(startMonth).year(Number(startYear)).format("YYYY-MM-DD"),
                            };
                            if (isActive === "false") {
                                condition.endDate = moment().month(endMonth).year(Number(endYear)).format("YYYY-MM-DD");
                            }
                            if (type === "CHRONIC_CONDITION") {
                                condition.type = type;
                            } else {
                                condition.isPreventative = type === "PREVENTATIVES";
                            }
                        } else {
                            condition = {
                                ...values,
                                date: moment().format("YYYY-MM-DD"),
                                isActive: true,
                                type: type,
                            };
                        }
                        handleSubmit(condition);
                        setOpenDialog(false);
                    }}
                    validationSchema={validationSchema}
                >
                    {({handleChange, values, handleBlur, setFieldValue}) => {
                        return (
                            <Form>
                                <Grid container spacing={2} alignItems="center"
                                    className={classes.formItem}>
                                    <Grid item xs={12} sm={type === "ALLERGY" ? 3 : 2}>
                                        <Typography variant="h4"
                                            className={classes.subtitle}>{nameFieldTitles[type]}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={type === "ALLERGY" ? 9 : 10}>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            fullWidth
                                            name="name"
                                            type="text"
                                            InputProps={{
                                                style: {
                                                    fontSize: 18,
                                                    height: 48,
                                                    color: "#000000"
                                                }
                                            }}
                                            onChange={handleChange("name")}
                                            onBlur={handleBlur("name")}
                                            value={values.name}
                                            placeHolder={type === "ALLERGY" && "Example: Vomiting, hives, diarrhea..."}
                                        />
                                    </Grid>
                                </Grid>
                                {type === "ALLERGY"
                                    ?
                                    <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        className={classes.formItem}
                                    >
                                        <Grid item xs={12} sm={4}>
                                            <Typography
                                                variant="h4"
                                                className={classes.subtitle}
                                            >
                                                    Describe
                                                    what happened
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                fullWidth
                                                name="notes"
                                                type="text"
                                                InputProps={{
                                                    style: {
                                                        fontSize: 18,
                                                        height: 48,
                                                        color: "#000000"
                                                    }
                                                }}
                                                onChange={handleChange("note")}
                                                onBlur={handleBlur("note")}
                                                value={values.note}
                                                placeHolder="Example: Vomiting, hives, diarrhea..."
                                            />
                                        </Grid>
                                    </Grid>
                                    :
                                    <>
                                        {type !== "CHRONIC_CONDITION"
                                            &&
                                            <Grid container spacing={2} justify="flex-start" alignItems="center"
                                                className={classes.formItem}>
                                                <Grid item xs={12} sm={5} container alignItems="center">
                                                    <Grid item xs={12} sm={5}>
                                                        <Typography variant="h4" style={{fontSize: 18}}>Amount</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={7}>
                                                        <Field
                                                            component={TextField}
                                                            variant="outlined"
                                                            fullWidth
                                                            name="amount"
                                                            type="text"
                                                            InputProps={{
                                                                style: {
                                                                    height: 48,
                                                                    color: "#000000",
                                                                    fontSize: 18
                                                                }
                                                            }}
                                                            onChange={handleChange("amount")}
                                                            onBlur={handleBlur("amount")}
                                                            value={values.amount}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12} sm={7} container alignItems="center">
                                                    <Grid item xs={12} sm={5}>
                                                        <Typography variant="h4" style={{fontSize: 18}}>How often do you
                                                            give?</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={7}>
                                                        <Field
                                                            component={TextField}
                                                            variant="outlined"
                                                            fullWidth
                                                            name="howOften"
                                                            type="text"
                                                            InputProps={{
                                                                style: {
                                                                    height: 48,
                                                                    color: "#000000",
                                                                    fontSize: 18
                                                                }
                                                            }}
                                                            onChange={handleChange("howOften")}
                                                            onBlur={handleBlur("howOften")}
                                                            value={values.howOften}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        }

                                        <Grid container spacing={2} justify="flex-start" alignItems="center"
                                            className={classes.formItem}>
                                            <Grid item xs={12} sm={6} container alignItems="center">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography variant="h4" style={{fontSize: 18}}>Start
                                                            Date</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <DatePicker
                                                        views={["month"]}
                                                        format="MMMM"
                                                        value={new Date(values.startMonth)}
                                                        handleChange={(val) => {
                                                            setFieldValue("startMonth", val.getMonth());
                                                            setStartDate(prev => ({
                                                                ...prev,
                                                                month: val.getMonth()
                                                            }));
                                                        }}
                                                        inputProps={datePickerInputProps}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} sm={6} container alignItems="center">
                                                <Grid item xs={12} sm={3}>
                                                    <Typography variant="h4" style={{fontSize: 18}}>Year</Typography>
                                                </Grid>
                                                <Grid item xs={12} sm={9}>
                                                    <DatePicker
                                                        views={["year"]}
                                                        format="yyyy"
                                                        disableFuture={true}
                                                        maxDate={new Date()}
                                                        value={new Date(values.startYear)}
                                                        handleChange={(val) => {
                                                            setFieldValue("startYear", val.getFullYear());
                                                            setStartDate(prev => ({
                                                                ...prev,
                                                                year: val.getFullYear(),
                                                            }));
                                                        }}
                                                        inputProps={datePickerInputProps}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid container justify="flex-start" alignItems="center"
                                            className={classes.formItem}>
                                            <Grid item xs={12}>
                                                <Typography variant="h4" className={classes.subtitle}>{type === "MEDICATIONS"
                                                    ? "Is this a current medication?"
                                                    : type === "PREVENTATIVES"
                                                        ? "Is this a current preventative? "
                                                        : "Is this a current issue?"}</Typography>
                                                <Field
                                                    component={RadioGroup}
                                                    name="isActive"
                                                    value={values.isActive}
                                                    row
                                                >
                                                    <FormControlLabel
                                                        value="true"
                                                        onChange={() => {
                                                            setFieldValue("isActive", "true");
                                                            setIsCurrent(true);
                                                        }}
                                                        control={<Radio/>}
                                                        label="Yes"
                                                    />
                                                    <FormControlLabel
                                                        onChange={() => {
                                                            setFieldValue("isActive", "false");
                                                            setIsCurrent(false);
                                                        }}
                                                        value="false"
                                                        control={<Radio/>}
                                                        label="No"
                                                    />
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        {
                                            !isCurrent
                                                &&
                                                <Grid container spacing={2} justify="flex-start" alignItems="center"
                                                    className={classes.formItem}>
                                                    <Grid item xs={12} sm={6} container alignItems="center">
                                                        <Grid item xs={12} sm={3}>
                                                            <Typography variant="h4" style={{fontSize: 18}}>End
                                                                Date</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={9}>
                                                            <DatePicker
                                                                views={["month"]}
                                                                format="MMMM"
                                                                value={values.endMonth?.length ? new Date(values.endMonth) : new Date()}
                                                                handleChange={(val) => {
                                                                    setFieldValue("endMonth", val.getMonth());
                                                                    setEndDate(prev => ({
                                                                        ...prev,
                                                                        month: val.getMonth(),
                                                                    }));
                                                                }}
                                                                inputProps={datePickerInputProps}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6} container alignItems="center">
                                                        <Grid item xs={12} sm={3}>
                                                            <Typography variant="h4"
                                                                style={{fontSize: 18}}>Year</Typography>
                                                        </Grid>
                                                        <Grid item xs={12} sm={9}>
                                                            <DatePicker
                                                                views={["year"]}
                                                                format="yyyy"
                                                                disableFuture={true}
                                                                maxDate={new Date()}
                                                                value={values.endYear?.length ? new Date(values.endYear) : new Date()}
                                                                handleChange={(val) => {
                                                                    setFieldValue("endYear", val.getFullYear());
                                                                    setEndDate(prev => ({
                                                                        ...prev,
                                                                        year: val.getFullYear(),
                                                                    }));
                                                                }}
                                                                inputProps={datePickerInputProps}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item xs={12} alignItems="center">
                                                        {dateErr &&
                                                        <Typography className={classes.formError}>Your end date cannot be
                                                            the same or before the start date</Typography>}
                                                    </Grid>
                                                </Grid>
                                        }
                                    </>
                                }
                                <Grid container justify="center" alignItems="center" spacing={1}>
                                    <Grid item sm={3} xs={12}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type="submit"
                                            classes={{root: classes.button}}
                                            fullWidth
                                        >
                                                Save
                                        </Button>
                                    </Grid>
                                    <Grid item sm={3} xs={12}>
                                        <Button
                                            onClick={handleClose}
                                            variant="contained"
                                            classes={{root: classes.button}}
                                            fullWidth
                                        >
                                                Cancel
                                        </Button>
                                    </Grid>
                                    {itemToEdit &&
                                        <Grid item sm={3} xs={12}>
                                            <Button
                                                onClick={() => {
                                                    handleDelete(values);
                                                    setOpenDialog(false);
                                                }} variant="contained"
                                                classes={{root: classes.deleteButton}}
                                                fullWidth
                                            >
                                                Delete
                                            </Button>
                                        </Grid>
                                    }
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>
            </DialogContent>
        </Dialog>
    );
}
;

export default AllergiesIllnessesForm;
